import type { Space } from "../api/spaces";

export const CREATE_SPACE_DRAFT_KEY = "blackspace.spaces.createDraft.v1";
export const MAX_SPACE_TAGS = 20;
export const MIN_ACCESS_CODE_LENGTH = 10;

export type CreateSpaceAccessMode = "open" | "locked" | "private";

export type CreateSpaceDraft = {
    title: string;
    desc: string;
    accentColor: string;
    tags: string[];
    tagDraft: string;
    accessMode: CreateSpaceAccessMode;
    lockedReason: string;
    inviteExpiresAt: string;
};

export type CreateSpaceValidationInput = CreateSpaceDraft & {
    inviteCode: string;
    now?: number;
};

export function normalizeSpaceTag(value: string) {
    return value.trim().replace(/^#+/, "").replace(/\s+/g, "-").toLowerCase().slice(0, 32);
}

export function finalSpaceTags(tags: string[], tagDraft: string): string[] {
    return [...tags, normalizeSpaceTag(tagDraft)]
        .filter(Boolean)
        .filter((tag, index, values) => values.indexOf(tag) === index);
}

export function validateCreateSpace(input: CreateSpaceValidationInput): string | null {
    const title = input.title.trim();
    const desc = input.desc.trim();
    const code = input.inviteCode.trim();
    const tags = finalSpaceTags(input.tags, input.tagDraft);

    if (!title) return "Space title is required.";
    if (title.length > 100) return "Space title must be 100 characters or fewer.";
    if (desc.length > 500) return "Space description must be 500 characters or fewer.";
    if (!/^#[0-9a-fA-F]{6}$/.test(input.accentColor)) return "Choose a valid Space colour.";
    if (tags.length > MAX_SPACE_TAGS) return `Spaces can have up to ${MAX_SPACE_TAGS} tags.`;
    if (tags.some((tag) => tag.length > 32)) return "Space tags must be 32 characters or fewer.";

    if (input.accessMode !== "open") {
        if (code && (code.length < MIN_ACCESS_CODE_LENGTH || !/[a-z]/i.test(code) || !/\d/.test(code))) {
            return `Access codes must be at least ${MIN_ACCESS_CODE_LENGTH} characters and include a letter and number.`;
        }
        if (input.inviteExpiresAt) {
            const expiry = new Date(input.inviteExpiresAt).getTime();
            if (!Number.isFinite(expiry)) return "Choose a valid access-code expiry.";
            if (expiry <= (input.now ?? Date.now())) return "Access-code expiry must be in the future.";
        }
    }

    return null;
}

export function hasCreateSpaceDraftContent(draft: CreateSpaceDraft): boolean {
    return Boolean(
        draft.title.trim() ||
        draft.desc.trim() ||
        draft.tags.length ||
        draft.tagDraft.trim() ||
        draft.accentColor !== "#22d3ee" ||
        draft.accessMode !== "open" ||
        Boolean(draft.inviteExpiresAt)
    );
}

export function readCreateSpaceDraft(): Partial<CreateSpaceDraft> | null {
    try {
        const raw = localStorage.getItem(CREATE_SPACE_DRAFT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

        const draft: Partial<CreateSpaceDraft> = {};
        if (typeof parsed.title === "string") draft.title = parsed.title.slice(0, 100);
        if (typeof parsed.desc === "string") draft.desc = parsed.desc.slice(0, 500);
        if (typeof parsed.accentColor === "string") draft.accentColor = parsed.accentColor;
        if (Array.isArray(parsed.tags)) {
            draft.tags = parsed.tags
                .filter((tag: unknown): tag is string => typeof tag === "string")
                .map(normalizeSpaceTag)
                .filter(Boolean)
                .slice(0, MAX_SPACE_TAGS);
        }
        if (typeof parsed.tagDraft === "string") draft.tagDraft = parsed.tagDraft.slice(0, 32);
        if (["open", "locked", "private"].includes(parsed.accessMode)) {
            draft.accessMode = parsed.accessMode;
        }
        if (typeof parsed.inviteExpiresAt === "string") {
            draft.inviteExpiresAt = parsed.inviteExpiresAt.slice(0, 16);
        }
        return Object.keys(draft).length ? draft : null;
    } catch {
        return null;
    }
}

export function writeCreateSpaceDraft(draft: CreateSpaceDraft) {
    try {
        if (!hasCreateSpaceDraftContent(draft)) {
            localStorage.removeItem(CREATE_SPACE_DRAFT_KEY);
            return;
        }
        localStorage.setItem(CREATE_SPACE_DRAFT_KEY, JSON.stringify({ ...draft, lockedReason: "" }));
    } catch {
        // Draft persistence is best effort; creation must continue without storage.
    }
}

export function clearCreateSpaceDraft() {
    try {
        localStorage.removeItem(CREATE_SPACE_DRAFT_KEY);
    } catch {
        // Ignore storage restrictions.
    }
}

export type CreateSpaceRequest = {
    title: string;
    desc?: string;
    category?: string;
    accentColor?: string;
    isPublic?: boolean;
    accessMode?: "open" | "locked" | "private" | "sealed" | string;
    inviteCode?: string;
    inviteExpiresAt?: string | null;
    constitution?: Space["constitution"];
    rules?: string[];
    tags?: string[];
};
